import { Linkedin } from 'lucide-react';
import { BrandLogo } from './BrandLogo';
import { Mail } from './Icons';
import { useTranslation } from '../contexts/TranslationContext';
import { publicSocialLinks } from '../config/publicSocialLinks';
import { ADR_SECONDS_VIDEO_WATCH_PATH } from '../config/videoWatchSeo';
import { landingSectionClassName } from './landingSectionClassName';
import {
  AI_RELIABILITY_PATH,
  ASSISTANT_ADR_PATH,
  CONSULTANT_AGENT_PATH,
  TRANSPORT_ANALYZER_PATH,
  WASTE_OPERATORS_PATH,
  COOKIE_POLICY_PATH,
  PRIVACY_POLICY_PATH,
  TERMS_OF_SERVICE_PATH,
  SECURITY_TRUST_PATH,
  localizePublicHref,
} from '../utils/publicRoutes';

interface LandingFooterProps {
  onContactClick: () => void;
  onGetStarted?: () => void;
}

type FooterLink = {
  labelKey: string;
  href: string;
  external?: boolean;
} | {
  labelKey: string;
  action: 'contact';
};

interface FooterLinkColumn {
  titleKey: string;
  links: FooterLink[];
}

const FOOTER_LINK_COLUMNS = [
  {
    titleKey: 'footer.solutionsTitle',
    links: [
      { labelKey: 'footer.consultantAgentLink', href: CONSULTANT_AGENT_PATH },
      { labelKey: 'footer.transportAnalyzerLink', href: TRANSPORT_ANALYZER_PATH },
      { labelKey: 'footer.wasteOperatorsLink', href: WASTE_OPERATORS_PATH },
    ],
  },
  {
    titleKey: 'footer.resourcesTitle',
    links: [
      { labelKey: 'footer.faqLink', href: '/#faq' },
      { labelKey: 'footer.assistantAdrLink', href: ASSISTANT_ADR_PATH },
      { labelKey: 'footer.aiReliabilityLink', href: AI_RELIABILITY_PATH },
      { labelKey: 'footer.securityTrustLink', href: SECURITY_TRUST_PATH },
    ],
  },
  {
    titleKey: 'footer.companyTitle',
    links: [
      { labelKey: 'footer.quickVideoLink', href: ADR_SECONDS_VIDEO_WATCH_PATH },
      { labelKey: 'footer.manifestoLink', href: '/manifesto' },
      { labelKey: 'contact.link', action: 'contact' },
      { labelKey: 'footer.linkedin', href: publicSocialLinks.linkedinProfileUrl, external: true },
    ],
  },
] satisfies FooterLinkColumn[];

function isContactFooterLink(link: FooterLink): link is Extract<FooterLink, { action: 'contact' }> {
  return 'action' in link && link.action === 'contact';
}

export function LandingFooter({ onContactClick, onGetStarted }: LandingFooterProps) {
  const { language, t } = useTranslation();

  return (
    <footer className={landingSectionClassName({ className: 'px-3 py-10 sm:px-6 sm:py-14', fillAvailable: false })}>
      <div className="relative mx-auto max-w-7xl">
        <div className="pointer-events-none absolute -left-36 -top-44 hidden h-96 w-96 rounded-full bg-[rgb(var(--theme-primary)/0.28)] blur-3xl sm:block" data-footer-glow aria-hidden="true" />
        <div className="pointer-events-none absolute -right-32 -top-48 hidden h-[28rem] w-[28rem] rounded-full bg-[rgb(var(--theme-secondary)/0.3)] blur-3xl sm:block" data-footer-glow aria-hidden="true" />
        <div className="pointer-events-none absolute inset-x-10 bottom-0 hidden h-40 rounded-full bg-[rgb(var(--theme-highlight)/0.22)] blur-3xl sm:block" data-footer-glow aria-hidden="true" />

        <div className="theme-public-footer-card relative overflow-hidden rounded-xl border px-5 pb-5 pt-6 shadow-2xl sm:px-8 sm:pb-7 sm:pt-8">
          <div className="grid gap-8 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-[minmax(7rem,1fr)_repeat(3,minmax(0,12rem))]">
            <div className="flex justify-center sm:col-span-2 md:col-span-3 lg:col-span-1 lg:justify-start">
              <div className="flex flex-col items-center gap-3 lg:items-start">
                <BrandLogo variant="mark" className="h-12 w-12 sm:h-14 sm:w-14" loading="lazy" />
                {language === 'fr' && (
                  <div data-testid="footer-french-tech-support" className="flex max-w-48 flex-col items-center gap-1.5 text-center sm:flex-row sm:gap-2 lg:text-left">
                    <span data-testid="footer-french-flag" className="shrink-0 text-base leading-none" aria-hidden="true">🇫🇷</span>
                    <p className="theme-public-footer-muted text-xs font-bold leading-relaxed">
                      {t('footer.frenchTechSupportPrefix')}{' '}
                      <a
                        href="/register"
                        onClick={(event) => {
                          if (!onGetStarted) return;
                          event.preventDefault();
                          onGetStarted();
                        }}
                        className="brand-link brand-focus-ring underline decoration-[rgb(var(--theme-primary)/0.5)] underline-offset-2 theme-public-footer-link-action"
                      >
                        {t('footer.frenchTechSupportAction')}
                      </a>
                      {' !'}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {FOOTER_LINK_COLUMNS.map((column) => (
              <nav key={column.titleKey} aria-label={t(column.titleKey)} className="text-center lg:text-left">
                <h2 className="theme-public-footer-heading mb-4 text-[10px] font-black uppercase tracking-[0.18em]">
                  {t(column.titleKey)}
                </h2>
                <ul className="theme-public-footer-link space-y-2 text-xs font-medium">
                  {column.links.map((link) => {
                    const isLinkedin = link.labelKey === 'footer.linkedin';
                    return (
                      <li key={link.labelKey}>
                        {isContactFooterLink(link) ? (
                          <button
                            type="button"
                            onClick={onContactClick}
                            className="brand-link inline-flex min-h-6 items-center transition-colors theme-public-footer-link-action"
                          >
                            {t(link.labelKey)}
                          </button>
                        ) : (
                          <a
                            href={link.external ? link.href : localizePublicHref(link.href, language)}
                            target={link.external ? '_blank' : undefined}
                            rel={link.external ? 'noopener noreferrer' : undefined}
                            className={isLinkedin
                              ? 'brand-focus-ring theme-public-footer-icon inline-flex h-11 w-11 items-center justify-center rounded-lg transition-transform hover:-translate-y-0.5 motion-reduce:transform-none motion-reduce:transition-none'
                              : 'brand-link transition-colors theme-public-footer-link-action'}
                            aria-label={isLinkedin ? t('footer.linkedinAria') : undefined}
                          >
                            {isLinkedin
                              ? <Linkedin className="h-5 w-5 brand-linkedin-icon" aria-hidden="true" />
                              : t(link.labelKey)}
                          </a>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </nav>
            ))}
          </div>

          <div className="theme-public-footer-wordmark mt-16 select-none text-[clamp(3.6rem,15vw,10.5rem)] font-semibold leading-none tracking-[-0.08em] sm:mt-24">
            palen-adr.eu<span className="brand-text-strong">.</span>
          </div>

          <div className="theme-public-footer-muted mt-5 flex flex-col gap-5 text-[11px] sm:flex-row sm:items-center sm:justify-between">
            <div className="max-w-36 self-center text-center leading-tight sm:self-auto sm:text-left">
              <p className="uppercase">
              {t('footer.copyright')}
              </p>
              <p className="mt-1">
                {t('footer.platformBuiltBy')}{' '}
                <a
                  href="https://kairisio.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="brand-link transition-colors theme-public-footer-link-action"
                >
                  Kairisio.com
                </a>
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 sm:justify-start">
              <a
                href={localizePublicHref(PRIVACY_POLICY_PATH, language)}
                className="brand-link transition-colors theme-public-footer-link-action"
                aria-label={t('footer.privacyPolicy')}
              >
                {t('footer.privacyPolicy')}
              </a>
              <a
                href={localizePublicHref(TERMS_OF_SERVICE_PATH, language)}
                className="brand-link transition-colors theme-public-footer-link-action"
                aria-label={t('footer.termsOfService')}
              >
                {t('footer.termsOfService')}
              </a>
              <a
                href={localizePublicHref(COOKIE_POLICY_PATH, language)}
                className="brand-link transition-colors theme-public-footer-link-action"
                aria-label={t('footer.cookiePolicy')}
              >
                {t('footer.cookiePolicy')}
              </a>
            </div>

            <div className="flex items-center justify-center gap-3 sm:justify-start">
              <button
                type="button"
                onClick={onContactClick}
                className="theme-public-footer-icon inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors"
                aria-label={t('contact.link')}
              >
                <Mail className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
